import { buildMatchingContext } from "./matchingContext";
import { evaluateRule } from "./ruleEvaluator";
import { TREATMENT_AUTHORITY_RESPONSE_KEY } from "./treatmentApiConstants";

const CHILD_KEYS = ["rules", "conditions", "subgroups", "children"];

const combined = (operator, values) => {
  if (operator === "and") {
    if (values.includes(false)) return false;
    return values.includes(null) ? null : true;
  }
  if (values.includes(true)) return true;
  return values.includes(null) ? null : false;
};

const possibleRule = (rule, context, hiddenAliases, recommendationsKnown) => {
  if (!rule || typeof rule !== "object" || !Object.keys(rule).length) return true;
  if (rule.show_if || rule.skip_if) {
    const show = rule.show_if
      ? possibleRule(rule.show_if, context, hiddenAliases, recommendationsKnown)
      : true;
    const skip = rule.skip_if
      ? possibleRule(rule.skip_if, context, hiddenAliases, recommendationsKnown)
      : false;
    return combined("and", [show, skip === null ? null : !skip]);
  }

  if (CHILD_KEYS.some((key) => Object.prototype.hasOwnProperty.call(rule, key))) {
    const children = CHILD_KEYS.flatMap((key) => (
      Array.isArray(rule[key]) ? rule[key] : []
    ));
    if (!children.length) return true;
    const operator = String(
      rule.combinator || rule.logic || rule.operator
      || (rule.mode === "simple" ? "or" : "and"),
    ).toLowerCase();
    if (!["and", "or"].includes(operator)) return null;
    return combined(operator, children.map((child) => (
      possibleRule(child, context, hiddenAliases, recommendationsKnown)
    )));
  }

  const sourceField = rule.field || rule.question_id || rule.questionId;
  if (!sourceField || !rule.operator) return null;
  const field = rule.source === "patient_profile"
    ? `patient_profile.${sourceField}`
    : String(sourceField);
  if (
    field === TREATMENT_AUTHORITY_RESPONSE_KEY.recommendedProgramIds
    && !recommendationsKnown
  ) return null;
  const value = context[field];
  if (
    !Object.prototype.hasOwnProperty.call(context, field)
    || (value == null && !hiddenAliases.has(field))
  ) return null;
  return evaluateRule(rule, context);
};

const aliases = (question) => [
  question.id,
  question.source_id,
  question.source_question_id,
  question.release_node_id,
].filter((value) => value !== undefined && value !== null && value !== "")
  .map(String);

const rulesFor = (question) => [
  question.visibility_rules,
  question.visibilityRules,
  question.conditional_logic,
].find((rules) => rules && typeof rules === "object" && Object.keys(rules).length) || {};

export const countPossibleQuestions = ({ questionnaire, responses, authorityState, isProgressQuestion }) => {
  const questions = (questionnaire?.questions || []).filter(isProgressQuestion);
  const hiddenAliases = new Set();
  const recommendationsKnown = [
    "consents", "shipping", "product_selection", "checkout_ready",
    "checkout_consumed", "completed",
  ].includes(authorityState);
  let total = 0;

  questions.forEach((question) => {
    const context = buildMatchingContext(questionnaire, responses || {}, question);
    hiddenAliases.forEach((alias) => { context[alias] = null; });
    const outcome = possibleRule(
      rulesFor(question), context, hiddenAliases, recommendationsKnown,
    );
    if (outcome === false) {
      aliases(question).forEach((alias) => hiddenAliases.add(alias));
    } else {
      total += 1;
    }
  });
  return total;
};
